import { AxiosError } from "axios";
import { Dispatch } from "redux";
import { notification } from "antd";

import {
    IRetailMatchJobResult,
    IBrand,
    IRetailMatchJob,
    IRetailMatchJobImage,
    IStoreScoreRecord,
    IRetailMatchUpdateBrandLogoResponse,
} from "../Models/Models";
import { RetailMatchAxios } from "../Contracts/Axios/RetailMatchAxios";
import { IBrandCategory } from "../Models/IBrandCategory";
import { IRetailMatchGeography } from "../Models/IRetailMatchGeography";
import { RetailMatchGeographyOption } from "../Enums/RetailMatchGeographyOption";
import { ISubmitRetailMatchJobRequest } from "../Models/Requests/ISubmitRetailMatchJobRequest";
import { IAddRetailMatchImageRequest } from "../Models/Requests/IAddRetailMatchImageRequest";
import { IGetBrandsByGeographyAndCategoriesRequest } from "../Models/Requests/IGetBrandsByGeographyAndCategoriesRequest";

import {
    reverseGeocode,
    formatShortAddressFromGeocoder,
    getCityFromGeocoder,
    titleCase,
    getValidLogoPath,
    createBrandMap,
    createCategoryMap,
} from "../Components/Apps/RetailMatch/Logic/SharedMethods";
import {
    SET_RETAIL_MATCH_REPORT_MODAL_VISIBLE,
    START_RETAIL_MATCH_REPORT,
    GET_RETAIL_MATCH_RESULTS_SUCCESS,
    GET_RETAIL_MATCH_RESULTS_FAILURE,
    SET_RETAIL_MATCH_BENCHMARK_RECORD,
    SET_LOADING_BRANDS,
    GET_RETAIL_MATCH_BRANDS_SUCCESS,
    GET_RETAIL_MATCH_BRANDS_FAILURE,
    GET_RETAIL_MATCH_BRAND_SUCCESS,
    GET_RETAIL_MATCH_BRAND_FAILURE,
    SET_LOADING_UPDATE_RETAIL_MATCH_JOB,
    UPDATE_RETAIL_MATCH_JOB_SUCCESS,
    UPDATE_RETAIL_MATCH_JOB_FAILURE,
    ADD_RETAIL_MATCH_IMAGE_SUCCESS,
    ADD_RETAIL_MATCH_IMAGE_FAILURE,
    GET_RETAIL_MATCH_CATEGORIES_SUCCESS,
    GET_RETAIL_MATCH_CATEGORIES_FAILURE,
    SET_LOADING_CATEGORIES,
    GET_RETAIL_MATCH_GEOGRAPHIES_SUCCESS,
    GET_RETAIL_MATCH_GEOGRAPHIES_FAILURE,
    SET_LOADING_RETAIL_MATCH_GEOGRAPHIES,
    SET_LOADING_RETAIL_MATCH_REPORT,
    SUBMIT_RETAIL_MATCH_REPORT_SUCCESS,
    SUBMIT_RETAIL_MATCH_REPORT_FAILURE,
    CLEAR_RETAIL_MATCH_RESULTS,
    GET_BRANDS_SAMPLE_SET_DATA_SUCCESS,
    SET_LOADING_BRANDS_SAMPLE_SET_DATA,
    UPDATE_BRAND_LOGO_SUCCESS,
    SET_SELECTED_BRAND,
    SET_RETAIL_MATCH_MODAL_MARKER_CLUSTERER,
    UPDATE_RETAIL_MATCH_SITE_DETAILS_SUCCESS,
} from "./Types";
import { addRunningInsight } from "./GPSInsightsActions";
import { setMapCenter } from "./ReportMapActions";
import { IGetBrandsSampleSetDataByClosestRequest } from "../Models/Requests/IGetBrandsSampleSetDataByClosestRequest";
import { IBrandSampleSet } from "../Models/IBrandSampleSet";
import { createGenericIcon } from "../Components/Common/GenericIcon/GenericIcon.Helpers";
import { IGetBrandsSampleSetDataByGeoRequest } from "../Models/Requests/IGetBrandsSampleSetDataByGeoRequest";
import { GPSServiceAxios } from "../Contracts/Axios/GPSServiceAxios";
import { IRetailMatchUpdateBrandLogoRequest } from "../Models/Requests/IRetailMatchUpdateBrandLogoRequest";

export const setRetailMatchReportModalVisible = (visible: boolean) => ({
    type: SET_RETAIL_MATCH_REPORT_MODAL_VISIBLE,
    data: visible,
});

export const startRetailMatchReport = () => ({
    type: START_RETAIL_MATCH_REPORT,
    data: null,
});

export const getRetailMatchResultsSuccess = (data: IRetailMatchJobResult) => ({
    type: GET_RETAIL_MATCH_RESULTS_SUCCESS,
    data: data,
});

const getRetailMatchResultsFailure = (message: string) => ({
    type: GET_RETAIL_MATCH_RESULTS_FAILURE,
    data: message,
});

export const setRetailMatchBenchmarkRecord = (
    benchmark: IStoreScoreRecord
) => ({
    type: SET_RETAIL_MATCH_BENCHMARK_RECORD,
    data: benchmark,
});

export const clearRetailMatchResults = () => ({
    type: CLEAR_RETAIL_MATCH_RESULTS,
});

export const setSelectedBrand = (brand: IBrand) => ({
    type: SET_SELECTED_BRAND,
    data: brand,
});

export const setModalMarkerClusterer = (markerClusterer: MarkerClusterer) => ({
    type: SET_RETAIL_MATCH_MODAL_MARKER_CLUSTERER,
    data: markerClusterer,
});

const getMaxIndex = (index: number): number => Math.max(0, index);

export const formatResults = (
    response: IRetailMatchJobResult
): IRetailMatchJobResult => {
    const { overview, storeScores } = response.chartData;

    //index values need to be rounded for the UI
    for (const o of overview) {
        o.avgSampleVolumeIndex = getMaxIndex(Math.round(o.avgSampleVolumeIndex));
        o.coreCustomerCorrelationIndex = getMaxIndex(
            Math.round(o.coreCustomerCorrelationIndex)
        );
        o.coreCustomerCountIndex = getMaxIndex(
            Math.round(o.coreCustomerCountIndex)
        );
        o.coreCustomerPerHouseholdIndex = getMaxIndex(
            Math.round(o.coreCustomerPerHouseholdIndex)
        );
    }

    for (const score of storeScores) {
    //keep rounding
        score.vCntIdx = getMaxIndex(Math.round(score.vCntIdx));
        score.ccCntIdx = getMaxIndex(Math.round(score.ccCntIdx));
        score.ccCorrIdx = getMaxIndex(Math.round(score.ccCorrIdx));
        score.ccPerHHCntIdx = getMaxIndex(Math.round(score.ccPerHHCntIdx));

        score.addr = titleCase(score.addr);
        score.city = titleCase(score.city);
    }

    storeScores.sort((a, b) => b.ccCntIdx - a.ccCntIdx);

    return response;
};

export const submitRetailMatchResults = async (
    data: IRetailMatchJobResult,
    dispatch: any
) => {
    data = formatResults(data);

    const allBenchmarkRecords = data.chartData.storeScores.filter((store) => {
        return store.sid === "-1";
    });
    const otherStores = data.chartData.storeScores.filter((store) => {
        return !(
            store.lat === data.job.siteLatitude
      && store.lon === data.job.siteLongitude
        );
    });

    data.chartData.benchmarkRecords = allBenchmarkRecords;
    data.chartData.storeScores = otherStores;

    const benchmarks = await reverseGeocodeBenchmarks(data, dispatch);

    /** For each brand, create a logo if the supplied image is bad */
    for (const brand of data.job.brands) {
        const transformedPath = brand.logoPath?.concat("?size=176");
        brand.logoPath = await getValidLogoPath(transformedPath, brand.brandName);
    }

    const benchmark = benchmarks[0];

    dispatch(setRetailMatchBenchmarkRecord(benchmark));
    dispatch(setMapCenter(new google.maps.LatLng(benchmark.lat, benchmark.lon)));
    dispatch(getRetailMatchResultsSuccess(data));
};

export const reverseGeocodeBenchmarks = async (
    data: IRetailMatchJobResult,
    dispatch: any
): Promise<IStoreScoreRecord[]> => {
    const { chartData, job } = data;

    const benchmark
    = chartData.benchmarkRecords?.length > 0
        ? chartData.benchmarkRecords[0]
        : data.chartData.benchmarkRecords[0];

    if (benchmark) {
        const reverseGeocodeResults = await reverseGeocode(
            benchmark.lat,
            benchmark.lon
        );
        const benchmarkShortAddress = formatShortAddressFromGeocoder(
            reverseGeocodeResults
        );
        const reverseGeocodeCity = getCityFromGeocoder(
            reverseGeocodeResults
        )?.toUpperCase();

        benchmark.addr = titleCase(benchmarkShortAddress);
        if (!benchmark.city || benchmark.city === "") {
            benchmark.city = titleCase(reverseGeocodeCity);
        }

        //Set new address on all benchmarkRecords
        for (const b of chartData.benchmarkRecords) {
            b.addr = benchmark.addr;
            b.city = benchmark.city;
            b.st = benchmark.st;
            b.zip = benchmark.zip;
        }
    }

    return chartData.benchmarkRecords;
};

export const getResults = (jobId: string) => {
    return async (dispatch: Dispatch<any>) => {
        const retailMatchAxios: RetailMatchAxios = new RetailMatchAxios();

        dispatch(setLoadingRetailMatchReport(true));

        try {
            const response: IRetailMatchJobResult = await retailMatchAxios.GetResults(
                jobId
            );
            submitRetailMatchResults(response, dispatch);
        } catch (error) {
            dispatch(setRetailMatchBenchmarkRecord(null));
            dispatch(setLoadingRetailMatchReport(false));
            dispatch(getRetailMatchResultsFailure(error.message));

            notification.error({
                message: "Error getting Match Analysis Results",
                description: `There was a problem while retrieving the requested Match Analysis: ${error.message}.`,
                duration: null, // user must close
            });
        } finally {
            dispatch(setLoadingRetailMatchReport(false));
        }
    };
};

export const setLoadingBrands = (loading: boolean) => ({
    type: SET_LOADING_BRANDS,
    data: loading,
});

const getRetailMatchBrandsSuccess = (data: IBrand[]) => ({
    type: GET_RETAIL_MATCH_BRANDS_SUCCESS,
    data: data,
});

const getRetailMatchBrandsFailure = (error: AxiosError) => ({
    type: GET_RETAIL_MATCH_BRANDS_FAILURE,
    data: error.message,
});

export const getBrandsByNaicsCode = (naicsCode: string) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingBrands(true));

        const retailMatchAxios = new RetailMatchAxios();
        const promise = retailMatchAxios.GetBrandsByNaicsCode(naicsCode);

        promise
            .then((response: IBrand[]) => {
                dispatch(getRetailMatchBrandsSuccess(response));
            })
            .catch((error: AxiosError) => {
                dispatch(getRetailMatchBrandsFailure(error));
                notification.error({
                    message: "Error getting Brand",
                    description: error.message,
                    duration: null, // user must close
                });
            })
            .finally(() => {
                dispatch(setLoadingBrands(false));
            });
    };
};

export const getBrandsByGeoAndCategories = (
    request: IGetBrandsByGeographyAndCategoriesRequest
) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingBrands(true));

        const retailMatchAxios = new RetailMatchAxios();
        const promise = retailMatchAxios.GetBrandsByGeoAndCategory(request);

        promise
            .then((response: IBrand[]) => {
                dispatch(getRetailMatchBrandsSuccess(response));
            })
            .catch((error: AxiosError) => {
                dispatch(getRetailMatchBrandsFailure(error));
                notification.error({
                    message: "Error getting Brands",
                    description: error.message,
                    duration: null, // user must close
                });
            })
            .finally(() => {
                dispatch(setLoadingBrands(false));
            });
    };
};

export const getBrands = () => {
    return async (dispatch: Dispatch<any>) => {
        dispatch(setLoadingBrands(true));

        try {
            const gpsServiceAxios = new GPSServiceAxios();
            const response: IBrand[] = await gpsServiceAxios.GetBrands();

            dispatch(getRetailMatchBrandsSuccess(response));
        } catch (error) {
            dispatch(getRetailMatchBrandsFailure(error as AxiosError));
            notification.error({
                message: "Error getting Brands",
                description: (error as AxiosError).message,
                duration: null, // user must close
            });
        } finally {
            dispatch(setLoadingBrands(false));
        }
    };
};

const getRetailMatchBrandSuccess = (data: IBrand) => ({
    type: GET_RETAIL_MATCH_BRAND_SUCCESS,
    data: data,
});

const getRetailMatchBrandFailure = (error: AxiosError) => ({
    type: GET_RETAIL_MATCH_BRAND_FAILURE,
    data: error.message,
});

export const getBrand = (brandId: string) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingBrands(true));

        const gpsServiceAxios = new GPSServiceAxios();
        const promise = gpsServiceAxios.GetBrandById(brandId);

        promise
            .then((response: IBrand) => {
                dispatch(getRetailMatchBrandSuccess(response));
            })
            .catch((error: AxiosError) => {
                dispatch(getRetailMatchBrandFailure(error));
            })
            .finally(() => {
                dispatch(setLoadingBrands(false));
            });
    };
};

export const setLoadingUpdateRetailMatchJob = (loading: boolean) => ({
    type: SET_LOADING_UPDATE_RETAIL_MATCH_JOB,
    data: loading,
});

const updateJobSuccess = (job: Partial<IRetailMatchJob>) => ({
    type: UPDATE_RETAIL_MATCH_JOB_SUCCESS,
    data: job,
});

const updateJobFailure = (error: AxiosError) => ({
    type: UPDATE_RETAIL_MATCH_JOB_FAILURE,
    data: error.message,
});

const updateSiteDetailsSuccess = (siteDetails: string) => ({
    type: UPDATE_RETAIL_MATCH_SITE_DETAILS_SUCCESS,
    data: siteDetails,
});

export const updateJob = (job: Partial<IRetailMatchJob>, cb: () => void) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingUpdateRetailMatchJob(true));

        const retailMatchAxios = new RetailMatchAxios();
        const promise = retailMatchAxios.UpdateJob(job);

        promise
            .then(() => {
                dispatch(updateJobSuccess(job));
                cb();
            })
            .catch((error: AxiosError) => {
                dispatch(updateJobFailure(error));
            })
            .finally(() => {
                dispatch(setLoadingUpdateRetailMatchJob(false));
            });
    };
};

export const updateSiteDetails = (jobId: string, siteDetails: string, cb: () => void) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingUpdateRetailMatchJob(true));

        const retailMatchAxios = new RetailMatchAxios();
        const promise = retailMatchAxios.UpdateSiteDetails(jobId, siteDetails);

        promise
            .then(() => {
                dispatch(updateSiteDetailsSuccess(siteDetails));
                cb();
            })
            .catch((error: AxiosError) => {
                dispatch(updateJobFailure(error));
            })
            .finally(() => {
                dispatch(setLoadingUpdateRetailMatchJob(false));
            });
    };
};

const addImageSuccess = (image: IRetailMatchJobImage) => ({
    type: ADD_RETAIL_MATCH_IMAGE_SUCCESS,
    data: image,
});

const addImageFailure = (error: AxiosError) => ({
    type: ADD_RETAIL_MATCH_IMAGE_FAILURE,
    data: error.message,
});

const updateBrandLogoSuccess = (data: IRetailMatchUpdateBrandLogoResponse) => ({
    type: UPDATE_BRAND_LOGO_SUCCESS,
    data,
});

const imageUpdateSuccessMessage = "Your Image has been successfully updated.";

export const addImage = (
    request: IAddRetailMatchImageRequest,
    cb: () => void
) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingUpdateRetailMatchJob(true));

        const formData = new FormData();
        formData.append("jobId", request.jobId);
        formData.append("image", request.image);

        const retailMatchAxios = new RetailMatchAxios();
        const promise = retailMatchAxios.AddImage(formData);

        promise
            .then((response: IRetailMatchJobImage) => {
                cb();
                dispatch(addImageSuccess(response));
                notification.success({
                    message: "Updated",
                    description: imageUpdateSuccessMessage,
                });
            })
            .catch((error: AxiosError) => {
                notification.open({
                    type: "error",
                    message: `There was a problem while retrieving the requested Match Analysis: ${error.message}.`,
                });
                dispatch(addImageFailure(error));
            })
            .finally(() => {
                dispatch(setLoadingUpdateRetailMatchJob(false));
            });
    };
};

export const updateBrandLogo = (
    request: IRetailMatchUpdateBrandLogoRequest,
    cb: () => void
) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingUpdateRetailMatchJob(true));

        const formData = new FormData();
        formData.append("jobId", request.jobId);
        formData.append("brandId", request.brandId);
        formData.append("image", request.image);

        const retailMatchAxios = new RetailMatchAxios();
        const promise = retailMatchAxios.UpdateBrandLogo(formData);

        promise
            .then((response: string) => {
                cb();
                const data: IRetailMatchUpdateBrandLogoResponse = {
                    jobId: request.jobId,
                    brandId: request.brandId,
                    logoUrl: response,
                };
                dispatch(updateBrandLogoSuccess(data));
                notification.success({
                    message: "Updated",
                    description: imageUpdateSuccessMessage,
                });
            })
            .catch((error: AxiosError) => {
                notification.open({
                    type: "error",
                    message: `There was a problem while retrieving the requested Match Analysis: ${error.message}.`,
                });
                dispatch(addImageFailure(error));
            })
            .finally(() => {
                dispatch(setLoadingUpdateRetailMatchJob(false));
            });
    };
};

const getRetailMatchCategoriesSuccess = (data: IBrandCategory[]) => ({
    type: GET_RETAIL_MATCH_CATEGORIES_SUCCESS,
    data: data,
});

const getRetailMatchCategoriesFailure = (error: AxiosError) => ({
    type: GET_RETAIL_MATCH_CATEGORIES_FAILURE,
    data: error.message,
});

const setLoadingCategories = (loading: boolean) => ({
    type: SET_LOADING_CATEGORIES,
    data: loading,
});

export const getCategories = () => {
    return async (dispatch: Dispatch<any>) => {
        dispatch(setLoadingCategories(true));

        try {
            const gpsServiceAxios = new GPSServiceAxios();
            const response = await gpsServiceAxios.GetCategories();
            dispatch(getRetailMatchCategoriesSuccess(response));
        } catch (error) {
            dispatch(getRetailMatchCategoriesFailure(error as AxiosError));
            notification.error({
                message: "Error getting Categories",
                description: (error as AxiosError).message,
                duration: null, // user must close
            });
        } finally {
            dispatch(setLoadingCategories(false));
        }
    };
};

const getRetailMatchGeographiesSuccess = (data: IRetailMatchGeography[]) => ({
    type: GET_RETAIL_MATCH_GEOGRAPHIES_SUCCESS,
    data: data,
});

const getRetailMatchGeographiesFailure = (error: AxiosError) => ({
    type: GET_RETAIL_MATCH_GEOGRAPHIES_FAILURE,
    data: error.message,
});

const setLoadingRetailMatchGeographies = (loading: boolean) => ({
    type: SET_LOADING_RETAIL_MATCH_GEOGRAPHIES,
    data: loading,
});

export const getRetailMatchGeographies = (
    option: RetailMatchGeographyOption
) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingRetailMatchGeographies(true));

        const retailMatchAxios = new RetailMatchAxios();
        const promise = retailMatchAxios.GetGeographies(option);

        promise
            .then((response: IRetailMatchGeography[]) => {
                dispatch(getRetailMatchGeographiesSuccess(response));
            })
            .catch((error: AxiosError) => {
                dispatch(getRetailMatchGeographiesFailure(error));
                notification.error({
                    message: "Error getting Geographies",
                    description: error.message,
                    duration: null, // user must close
                });
            })
            .finally(() => {
                dispatch(setLoadingRetailMatchGeographies(false));
            });
    };
};

export const setLoadingRetailMatchReport = (loading: boolean) => ({
    type: SET_LOADING_RETAIL_MATCH_REPORT,
    data: loading,
});

const submitRetailMatchReportSuccess = () => ({
    type: SUBMIT_RETAIL_MATCH_REPORT_SUCCESS,
    data: null,
});

const submitRetailMatchReportFailure = (error: AxiosError) => ({
    type: SUBMIT_RETAIL_MATCH_REPORT_FAILURE,
    data: error.message,
});

export const submitRetailMatchReport = (
    request: ISubmitRetailMatchJobRequest
) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingRetailMatchReport(true));

        const retailMatchAxios = new RetailMatchAxios();
        const promise = retailMatchAxios.SubmitRetailMatchReport(request);

        promise
            .then((response: IRetailMatchJob) => {
                notification.success({
                    message: "Submitted!",
                    description:
            "Your Match Analysis is processing now. Check the reports section for status updates.",
                });
                dispatch(submitRetailMatchReportSuccess());
                addRunningInsight(response.jobId);
            })
            .catch((error: AxiosError) => {
                dispatch(submitRetailMatchReportFailure(error));
                notification.error({
                    message: "Error submitting report",
                    description: error.message,
                    duration: null, // user must close
                });
            })
            .finally(() => {
                dispatch(setLoadingRetailMatchReport(false));
                dispatch(setRetailMatchReportModalVisible(false));
            });
    };
};

export const checkSiteIsInDMA = (
    latitude: number,
    longitude: number
): Promise<boolean> => {
    const retailMatchAxios = new RetailMatchAxios();
    const promise = retailMatchAxios.SiteIsInDMA(latitude, longitude);

    return promise;
};

const getBrandsSampleSetDataSuccess = (data: IBrandSampleSet[]) => ({
    type: GET_BRANDS_SAMPLE_SET_DATA_SUCCESS,
    data: data,
});

export const setLoadingBrandsSampleSetData = (loading: boolean) => ({
    type: SET_LOADING_BRANDS_SAMPLE_SET_DATA,
    data: loading,
});

export const getBrandsSampleSetData = (
    request: IGetBrandsSampleSetDataByClosestRequest
) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingBrandsSampleSetData(true));

        const retailMatchAxios = new RetailMatchAxios();
        const promise = retailMatchAxios.GetBrandsSampleSetData(request);

        promise
            .then((response) => {
                dispatch(getBrandsSampleSetDataSuccess(response));
            })
            .catch((error: AxiosError) => {
                notification.error({
                    description: "Error",
                    message:
                        "Failed to get Brand Sample Set Data. Please contact SCOUT Support.",
                    duration: null, // user must close
                });
            });
    };
};

export const getSampleSetByGeo = (
    request: IGetBrandsSampleSetDataByGeoRequest
) => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingBrandsSampleSetData(true));

        const retailMatchAxios = new RetailMatchAxios();
        const promise = retailMatchAxios.GetSampleSetByGeo(request);

        promise
            .then((response) => {
                dispatch(getBrandsSampleSetDataSuccess(response));
            })
            .catch((error: AxiosError) => {
                notification.error({
                    description: "Error",
                    message:
                        "Failed to get Brand Sample Set Data. Please contact SCOUT Support.",
                    duration: null, // user must close
                });
            })
            .finally(() => {
                dispatch(setLoadingBrandsSampleSetData(false));
            });
    };
};
